// src/components/DynamicForm.jsx

import React from 'react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { get } from 'lodash';
import './DynamicForm.css';

const DynamicForm = ({
    schema,
    data,
    onChange,
    errors = {},
    campaigns = []
}) => {

    const handleFieldChange = (fieldId, value) => {
        onChange(fieldId, value);
    };


    /*
     * Get nested array value
     */
    const getRepeatableValue = (fieldId) => {
        const value = get(data, fieldId, []);

        return Array.isArray(value) ? value : [];
    };


    /*
     * Add new repeatable item
     */
    const addRepeatableItem = (field) => {

        const items = getRepeatableValue(field.id);

        if (
            field.maxItems &&
            items.length >= field.maxItems
        ) {
            return;
        }

        const newItem = {};

        field.fields?.forEach((subField) => {
            newItem[subField.id] = '';
        });

        const updatedItems = [
            ...items,
            newItem
        ];

        handleFieldChange(
            field.id,
            updatedItems
        );
    };


    /*
     * Remove repeatable item
     */
    const removeRepeatableItem = (field, index) => {

        const items = getRepeatableValue(field.id);

        if (
            field.minItems &&
            items.length <= field.minItems
        ) {
            return;
        }

        const updatedItems = items.filter(
            (_, itemIndex) => itemIndex !== index
        );

        handleFieldChange(
            field.id,
            updatedItems
        );
    };


    /*
     * Change repeatable item field
     */
    const handleRepeatableFieldChange = (
        field,
        index,
        subField,
        value
    ) => {

        const items = getRepeatableValue(field.id);

        const updatedItems = items.map(
            (item, itemIndex) => {

                if (itemIndex !== index) {
                    return item;
                }

                return {
                    ...item,
                    [subField.id]: value
                };
            }
        );

        handleFieldChange(
            field.id,
            updatedItems
        );
    };


    /*
     * Render normal field
     */
    const renderField = (field) => {

        const {
            id,
            type,
            label,
            options,
            placeholder = '',
            required,
            minLength,
            maxLength,
            condition,
        } = field;


        const value = get(data, id, '');

        const error = errors[id];


        // Conditional visibility
        if (condition) {

            const parentValue = get(
                data,
                condition.field
            );

            if (
                parentValue !== condition.value
            ) {
                return null;
            }
        }


        // Base props
        const inputProps = {
            value: value ?? '',

            onChange: (e) => {

                const val = e.target.value;

                handleFieldChange(
                    id,
                    val
                );
            },

            placeholder: placeholder,

            className: `dynamic-input ${
                error ? 'has-error' : ''
            }`,
        };


        let inputElement;


        switch (type) {

            case 'select':

                const selectOptions =
                    (
                        id === 'camp_name' &&
                        campaigns.length > 0
                    )
                        ? campaigns
                        : options;


                inputElement = (
                    <div className="relative">

                        <select
                            {...inputProps}
                            className="appearance-none"
                        >

                            <option value="">
                                Select...
                            </option>

                            {selectOptions?.map(
                                (opt) => (
                                    <option
                                        key={opt}
                                        value={opt}
                                    >
                                        {opt}
                                    </option>
                                )
                            )}

                        </select>


                        <ChevronDown
                            size={18}
                            className="
                                absolute
                                right-3
                                top-1/2
                                -translate-y-1/2
                                text-gray-500
                                pointer-events-none
                            "
                        />

                    </div>
                );

                break;


            case 'color':

                inputElement = (
                    <div className="color-input-group">

                        <input
                            type="color"
                            {...inputProps}
                            className="dynamic-color-picker"
                            value={
                                value || '#ffffff'
                            }
                        />

                        <input
                            type="text"
                            {...inputProps}
                            className="dynamic-color-text"
                            placeholder="#ffffff"
                        />

                    </div>
                );

                break;


            case 'textarea':

                inputElement = (
                    <textarea
                        {...inputProps}
                        rows="3"
                        className="dynamic-textarea"
                    />
                );

                break;


            case 'url':

                inputElement = (
                    <input
                        type="url"
                        {...inputProps}
                        className="dynamic-input"
                    />
                );

                break;


            case 'number':

                inputElement = (
                    <input
                        type="number"
                        {...inputProps}
                        className="dynamic-input"
                        min="0"
                        max="360"
                    />
                );

                break;


            default:

                inputElement = (
                    <input
                        type="text"
                        {...inputProps}
                        className="dynamic-input"
                    />
                );
        }


        return (
            <div
                key={id}
                className="form-group"
            >

                <label className="dynamic-label">

                    {label}

                    {required && (
                        <span className="required-star">
                            *
                        </span>
                    )}

                </label>


                {inputElement}


                {minLength && maxLength && (
                    <p className="field-hint">
                        Min: {minLength} characters,
                        Max: {maxLength} characters
                    </p>
                )}


                {error && (
                    <p className="field-error">
                        {error}
                    </p>
                )}

            </div>
        );
    };


    /*
     * Render repeatable field
     */
    const renderRepeatableField = (field) => {

        const items = getRepeatableValue(
            field.id
        );


        return (
            <div
                key={field.id}
                className="repeatable-field"
            >

                {items.map(
                    (item, index) => (

                        <div
                            key={index}
                            className="repeatable-item"
                        >

                            {/* Header */}

                            <div className="repeatable-header">

                                <h4>
                                    {field.itemLabel ||
                                        'Item'}{' '}

                                    {index + 1}
                                </h4>


                                <button
                                    type="button"
                                    onClick={() =>
                                        removeRepeatableItem(
                                            field,
                                            index
                                        )
                                    }
                                    disabled={
                                        field.minItems &&
                                        items.length <=
                                            field.minItems
                                    }
                                    className="
                                        repeatable-remove
                                    "
                                >

                                    <Trash2
                                        size={16}
                                    />

                                    Remove

                                </button>

                            </div>


                            {/* Fields */}

                            <div className="repeatable-fields">

                                {field.fields?.map(
                                    (subField) => {

                                        const subValue =
                                            item?.[
                                                subField.id
                                            ] ?? '';

                                        const subError =
                                            errors?.[
                                                `${field.id}.${index}.${subField.id}`
                                            ];


                                        return (
                                            <div
                                                key={
                                                    subField.id
                                                }
                                                className="form-group"
                                            >

                                                <label className="dynamic-label">

                                                    {subField.label}

                                                    {subField.required && (
                                                        <span className="required-star">
                                                            *
                                                        </span>
                                                    )}

                                                </label>


                                                {subField.type ===
                                                'textarea' ? (

                                                    <textarea
                                                        value={
                                                            subValue
                                                        }
                                                        onChange={(e) =>
                                                            handleRepeatableFieldChange(
                                                                field,
                                                                index,
                                                                subField,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={
                                                            subField.placeholder ||
                                                            ''
                                                        }
                                                        rows="3"
                                                        className={`dynamic-textarea ${
                                                            subError
                                                                ? 'has-error'
                                                                : ''
                                                        }`}
                                                    />

                                                ) : (

                                                    <input
                                                        type={
                                                            subField.type ===
                                                            'url'
                                                                ? 'url'
                                                                : 'text'
                                                        }
                                                        value={
                                                            subValue
                                                        }
                                                        onChange={(e) =>
                                                            handleRepeatableFieldChange(
                                                                field,
                                                                index,
                                                                subField,
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={
                                                            subField.placeholder ||
                                                            ''
                                                        }
                                                        className={`dynamic-input ${
                                                            subError
                                                                ? 'has-error'
                                                                : ''
                                                        }`}
                                                    />

                                                )}


                                                {subError && (
                                                    <p className="field-error">
                                                        {
                                                            subError
                                                        }
                                                    </p>
                                                )}

                                            </div>
                                        );
                                    }
                                )}

                            </div>

                        </div>
                    )
                )}


                {/* Add button */}

                <button
                    type="button"
                    onClick={() =>
                        addRepeatableItem(field)
                    }
                    disabled={
                        field.maxItems &&
                        items.length >=
                            field.maxItems
                    }
                    className="
                        repeatable-add
                    "
                >

                    <Plus size={18} />

                    Add {field.itemLabel || 'Item'}

                </button>


                {field.maxItems && (
                    <p className="field-hint">
                        {items.length} /{' '}
                        {field.maxItems} items
                    </p>
                )}

            </div>
        );
    };


    /*
     * Group fields by section
     */

    const sections = {};


    schema.forEach((field) => {

        const sectionName =
            field.section || 'General';


        if (!sections[sectionName]) {
            sections[sectionName] = [];
        }


        sections[sectionName].push(
            field
        );

    });


    return (
        <div className="dynamic-form">

            {Object.entries(sections).map(
                ([sectionName, fields]) => (

                    <div
                        key={sectionName}
                        className="form-section"
                    >

                        <h3 className="section-title">
                            {sectionName}
                        </h3>


                        <div className="section-fields">

                            {fields.map(
                                (field) => {

                                    if (
                                        field.type ===
                                        'repeatable'
                                    ) {
                                        return renderRepeatableField(
                                            field
                                        );
                                    }

                                    return renderField(
                                        field
                                    );
                                }
                            )}

                        </div>

                    </div>

                )
            )}

        </div>
    );
};


export default DynamicForm;